/**
 * ArcFace face alignment: the 2D similarity transform InsightFace calls `norm_crop`.
 *
 * Why this is necessary rather than nice to have: `svc-biometric-embedding` crops by bounding
 * box and discards the five face keypoints ("keypoints (unused for bbox crop)" in its own
 * source). ArcFace recognition models are trained exclusively on faces warped so that the
 * eyes, nose and mouth corners land on fixed pixel positions in a 112x112 tile. Feed such a
 * model a loosely-cropped face and the embedding varies with head pose about as much as it
 * varies with identity — which is precisely the failure observed: unrelated images scoring
 * 0.59-0.91 cosine, and a photograph of a different person passing a 0.36 threshold.
 *
 * Doing the warp on our side, before upload, restores the input distribution the model expects
 * without modifying a service shared with the customer verification journey.
 *
 * The estimator is the exact least-squares similarity transform (Umeyama without reflection),
 * expressed with complex arithmetic — for 2D that has a closed form and needs no SVD.
 */

export type Point2 = readonly [number, number];

/**
 * Canonical destination landmarks for a 112x112 ArcFace tile.
 *
 * These exact values are what the model was trained against; they are not tunable. Order is
 * image-left eye, image-right eye, nose tip, image-left mouth corner, image-right mouth corner.
 */
export const ARCFACE_DST_112: readonly Point2[] = [
  [38.2946, 51.6963],
  [73.5318, 51.5014],
  [56.0252, 71.7366],
  [41.5493, 92.3655],
  [70.7299, 92.2041],
];

export const ARCFACE_TILE_SIZE = 112;

/**
 * A 2D similarity transform: uniform scale, rotation and translation.
 *
 * Stored as the complex multiplier `(a + bi)` plus a translation, which maps
 * `(x, y) -> (a*x - b*y + tx, b*x + a*y + ty)`.
 *
 * Deliberately *not* a general affine transform. A similarity has 4 degrees of freedom and
 * cannot shear or stretch one axis, so it can only rigidly reposition the face — it cannot
 * distort one person's features into another's, which a full affine fit to five noisy points
 * can do.
 */
export interface SimilarityTransform {
  readonly a: number;
  readonly b: number;
  readonly tx: number;
  readonly ty: number;
}

/** Uniform scale factor implied by the transform. */
export function transformScale(t: SimilarityTransform): number {
  return Math.hypot(t.a, t.b);
}

/** Rotation in degrees implied by the transform. */
export function transformRotationDegrees(t: SimilarityTransform): number {
  return (Math.atan2(t.b, t.a) * 180) / Math.PI;
}

export function applyTransform(t: SimilarityTransform, p: Point2): Point2 {
  return [t.a * p[0] - t.b * p[1] + t.tx, t.b * p[0] + t.a * p[1] + t.ty];
}

/**
 * Least-squares similarity transform mapping `src` onto `dst`.
 *
 * Returns null when the source points are degenerate — coincident, collinear enough to leave
 * no scale information, or non-finite. A null must become "could not align", never a
 * best-effort warp: a bad warp produces a confident-looking embedding of a distorted face.
 */
export function estimateSimilarityTransform(
  src: readonly Point2[],
  dst: readonly Point2[],
): SimilarityTransform | null {
  if (src.length !== dst.length || src.length < 2) return null;

  let meanSrcX = 0;
  let meanSrcY = 0;
  let meanDstX = 0;
  let meanDstY = 0;
  for (let i = 0; i < src.length; i += 1) {
    const s = src[i] as Point2;
    const d = dst[i] as Point2;
    if (!Number.isFinite(s[0]) || !Number.isFinite(s[1])) return null;
    if (!Number.isFinite(d[0]) || !Number.isFinite(d[1])) return null;
    meanSrcX += s[0];
    meanSrcY += s[1];
    meanDstX += d[0];
    meanDstY += d[1];
  }
  const n = src.length;
  meanSrcX /= n;
  meanSrcY /= n;
  meanDstX /= n;
  meanDstY /= n;

  // z = sum(dstCentred * conj(srcCentred)) / sum(|srcCentred|^2)
  let numReal = 0;
  let numImag = 0;
  let den = 0;
  for (let i = 0; i < n; i += 1) {
    const sx = (src[i] as Point2)[0] - meanSrcX;
    const sy = (src[i] as Point2)[1] - meanSrcY;
    const dx = (dst[i] as Point2)[0] - meanDstX;
    const dy = (dst[i] as Point2)[1] - meanDstY;
    numReal += dx * sx + dy * sy;
    numImag += dy * sx - dx * sy;
    den += sx * sx + sy * sy;
  }

  if (!(den > 1e-9)) return null;

  const a = numReal / den;
  const b = numImag / den;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  // A collapsed scale means the points carried no usable geometry.
  if (Math.hypot(a, b) < 1e-6) return null;

  return {
    a,
    b,
    tx: meanDstX - (a * meanSrcX - b * meanSrcY),
    ty: meanDstY - (b * meanSrcX + a * meanSrcY),
  };
}

/** Mean residual, in destination pixels, after applying the transform. */
export function alignmentResidual(
  t: SimilarityTransform,
  src: readonly Point2[],
  dst: readonly Point2[],
): number {
  if (src.length !== dst.length || src.length === 0) return Number.POSITIVE_INFINITY;
  let total = 0;
  for (let i = 0; i < src.length; i += 1) {
    const mapped = applyTransform(t, src[i] as Point2);
    const target = dst[i] as Point2;
    total += Math.hypot(mapped[0] - target[0], mapped[1] - target[1]);
  }
  return total / src.length;
}

export interface AlignmentQuality {
  readonly transform: SimilarityTransform;
  readonly residualPx: number;
  readonly scale: number;
  readonly rotationDegrees: number;
  readonly acceptable: boolean;
  readonly reason: string;
}

/**
 * Estimate the transform and judge whether the result is worth embedding.
 *
 * A large residual means the five measured points do not form a face-shaped configuration —
 * usually a bad detection, a heavily rotated head, or a partly occluded face. Embedding that
 * anyway is how an unusable frame becomes a confident wrong answer.
 */
export function alignFivePoints(
  src: readonly Point2[],
  options: { maxResidualPx?: number; minScale?: number; maxScale?: number } = {},
): AlignmentQuality | null {
  const transform = estimateSimilarityTransform(src, ARCFACE_DST_112);
  if (transform === null) return null;

  const residualPx = alignmentResidual(transform, src, ARCFACE_DST_112);
  const scale = transformScale(transform);
  const maxResidualPx = options.maxResidualPx ?? 6;
  // A face occupying only a few pixels, or one blown up from almost nothing, carries no detail.
  const minScale = options.minScale ?? 0.15;
  const maxScale = options.maxScale ?? 6;

  let acceptable = true;
  let reason = 'alignment within tolerance';
  if (residualPx > maxResidualPx) {
    acceptable = false;
    reason = `landmark configuration does not fit a face template (residual ${residualPx.toFixed(1)}px)`;
  } else if (scale < minScale) {
    acceptable = false;
    reason = 'face is too small in frame to align reliably';
  } else if (scale > maxScale) {
    acceptable = false;
    reason = 'face fills too much of the frame to align reliably';
  }

  return {
    transform,
    residualPx,
    scale,
    rotationDegrees: transformRotationDegrees(transform),
    acceptable,
    reason,
  };
}
